import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { requestAPI } from '../../data/api'
import { getModel } from '../../data/Model'

// Muted styling for the client short name next to the client name
const mutedNoteStyle = { marginLeft: '8px', color: '#666', fontSize: '0.9em' }

class StateCounts extends Component {
	constructor(props) {
		super(props)
		const modelData = getModel().me.data
		const availableClients = (modelData && modelData.clients) ? modelData.clients : []
		this.state = {
			selectedState: 'contact_enrichment', // Default state
			violationsData: [],
			loading: false,
			sortColumn: null,
			sortDirection: 'desc',
			availableClients: [], // Will be populated from API response
			availableStates: [
				{ value: 'contact_enrichment', label: 'Contact Enrichment' },
				{ value: 'ready_for_export', label: 'Ready for Export' },
				{ value: 'client_approval', label: 'Partner Review' },
				{ value: 'stage_01', label: 'Stage 01' },
				{ value: 'stage_02', label: 'Stage 02' },
				{ value: 'stage_03', label: 'Stage 03' },
			],
		}
	}

	componentDidMount() {
		// Fetch statistics on component mount with default state
		this.fetchStatistics()
	}

	fetchStatistics = async () => {
		if (!this.state.selectedState) {
			return
		}

		this.setState({ loading: true })
		try {
			const { selectedState } = this.state
			const response = await requestAPI(`/violations/statecount/${selectedState}`, 'GET')

			// Cases a partner has put on hold need no action, so they are kept out of the web,
			// facebook, instagram and total counts and reported in their own column instead.
			const violationsData = Array.isArray(response)
				? response.map((item) => ({
					client_id: item.client_id,
					client_name: item.name || `Client ${item.client_id}`,
					client_short_name: item.short_name || '',
					violation_count_total: (Number(item.violations) || 0) - (Number(item.on_hold_count) || 0),
					violation_count_web: (Number(item.web_violations) || 0) - (Number(item.web_on_hold_count) || 0),
					violation_count_facebook: (Number(item.facebook_violations) || 0) - (Number(item.facebook_on_hold_count) || 0),
					violation_count_instagram: (Number(item.instagram_violations) || 0) - (Number(item.instagram_on_hold_count) || 0),
					cases_on_hold: Number(item.on_hold_count) || 0,
				}))
				: []

			const availableClients = violationsData.map(({ client_id, client_name, client_short_name }) => ({
				client_id,
				client_name,
				client_short_name,
			}))

			this.setState({ violationsData, availableClients, loading: false })
		} catch (error) {
			console.error('Failed to fetch statistics:', error)
			this.setState({ loading: false })
		}
	}

	getClientName = (clientId) => {
		// Try to get from current availableClients first
		let client = this.state.availableClients.find((c) => c.client_id === clientId)
		if (client) return client.client_name

		// Fallback to model data
		const modelData = getModel().me.data
		const modelClients = (modelData && modelData.clients) ? modelData.clients : []
		client = modelClients.find((c) => c.client_id === clientId)
		return client ? client.client_name : `Client ${clientId}`
	}

	handleSort = (column) => {
		const { sortColumn, sortDirection } = this.state

		// Clicking the active column flips the direction, a new column starts descending
		const newDirection = sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc'

		this.setState({ sortColumn: column, sortDirection: newDirection })
	}

	getSortedData = (sortColumn) => {
		const { violationsData, sortDirection } = this.state
		if (!sortColumn) {
			return violationsData
		}

		return [...violationsData].sort((a, b) => {
			if (sortColumn === 'client_name') {
				const aVal = (a[sortColumn] || '').toLowerCase()
				const bVal = (b[sortColumn] || '').toLowerCase()
				return sortDirection === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal)
			}

			const aVal = Number(a[sortColumn]) || 0
			const bVal = Number(b[sortColumn]) || 0
			return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
		})
	}

	handleStateSelect = (e) => {
		const selectedState = e.target.value
		this.setState({ selectedState }, () => {
			if (selectedState) {
				this.fetchStatistics()
			} else {
				this.setState({ violationsData: [], availableClients: [] })
			}
		})
	}

	render() {
		const { loading, selectedState, violationsData, availableStates, sortDirection } = this.state
		// Cases can only be put on hold by the partner while they are up for partner review
		const showOnHold = selectedState === 'client_approval'
		const totalOnHold = violationsData.reduce((sum, item) => sum + item.cases_on_hold, 0)
		// The on hold column disappears for the other states, so a sort on it no longer applies
		const sortColumn = this.state.sortColumn === 'cases_on_hold' && !showOnHold ? null : this.state.sortColumn
		const sortedData = this.getSortedData(sortColumn)

		const getSortIndicator = (column) => {
			if (sortColumn !== column) return null
			return sortDirection === 'desc' ? ' ↓' : ' ↑'
		}

		return (
			<div>
				<div className="statistics-controls">
					<div className="control-group">
						<label className="control-label">Select State</label>
						<select className="control-select" value={selectedState} onChange={this.handleStateSelect}>
							<option value="">-- Select a State --</option>
							{availableStates.map((state) => (
								<option key={state.value} value={state.value}>
									{state.label}
								</option>
							))}
						</select>
					</div>
				</div>

				{loading && (
					<div className="statistics-loading">
						<div className="loading-spinner"></div>
						<p>Loading statistics...</p>
					</div>
				)}

				{!loading && selectedState && (
					<div className="statistics-results">
						<h2 className="results-title">Violations in "{(() => {
							const state = availableStates.find(s => s.value === selectedState)
							return state ? state.label : selectedState
						})()}" State</h2>
						{violationsData.length > 0 ? (
							<>
							<table className="statistics-table">
								<thead>
									<tr>
										<th className="sortable-header" onClick={() => this.handleSort('client_name')}>
											Client Name{getSortIndicator('client_name')}
										</th>
										<th className="sortable-header" onClick={() => this.handleSort('violation_count_web')}>
											Web{getSortIndicator('violation_count_web')}
										</th>
										<th className="sortable-header" onClick={() => this.handleSort('violation_count_facebook')}>
											Facebook{getSortIndicator('violation_count_facebook')}
										</th>
										<th className="sortable-header" onClick={() => this.handleSort('violation_count_instagram')}>
											Instagram{getSortIndicator('violation_count_instagram')}
										</th>
										<th className="sortable-header" onClick={() => this.handleSort('violation_count_total')}>
											Total{getSortIndicator('violation_count_total')}
										</th>
										{showOnHold && (
											<th className="sortable-header" onClick={() => this.handleSort('cases_on_hold')}>
												On Hold{getSortIndicator('cases_on_hold')}
											</th>
										)}
									</tr>
								</thead>
								<tbody>
									{sortedData.map((item) => (
										<tr key={item.client_id}>
											<td>
												{item.client_name}
												{item.client_short_name && (
													<span style={mutedNoteStyle}>
														({item.client_short_name})
													</span>
												)}
											</td>
											<td className="count-cell">{item.violation_count_web}</td>
											<td className="count-cell">{item.violation_count_facebook}</td>
											<td className="count-cell">{item.violation_count_instagram}</td>
											<td className="count-cell">{item.violation_count_total}</td>
											{showOnHold && (
												<td className="count-cell on-hold-cell">{item.cases_on_hold}</td>
											)}
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr>
										<td className="total-label">Total</td>
										<td className="total-count">
											{violationsData.reduce((sum, item) => sum + item.violation_count_web, 0)}
										</td>
										<td className="total-count">
											{violationsData.reduce((sum, item) => sum + item.violation_count_facebook, 0)}
										</td>
										<td className="total-count">
											{violationsData.reduce((sum, item) => sum + item.violation_count_instagram, 0)}
										</td>
										<td className="total-count">
											{violationsData.reduce((sum, item) => sum + item.violation_count_total, 0)}
										</td>
										{showOnHold && (
											<td className="total-count on-hold-cell">{totalOnHold}</td>
										)}
									</tr>
								</tfoot>
							</table>
							{showOnHold && (
								<p className="table-note">
									On hold cases are listed separately and are not part of the web, facebook, instagram or total counts.
								</p>
							)}
							</>
						) : (
							<div className="no-data">
								<p>No violations found for the selected criteria.</p>
							</div>
						)}
					</div>
				)}
			</div>
		)
	}
}

StateCounts.contextTypes = {
	gettext: PropTypes.func.isRequired,
}

export default StateCounts
